import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';

import { hasClientRole } from '../session/session.model';
import { SessionService } from '../session/session.service';

/**
 * AITH-47 D-01: `/cost` is staff-only, and a client role that arrives there is
 * **relocated** to `/usage` rather than refused.
 *
 * Invoked by `costAccessGuard` on `/cost` after `sessionGuard` and before
 * `roleGuard`. Angular evaluates separate members of a `canActivate` array
 * concurrently, so this sequence lives in one composite guard: `roleGuard`
 * records denial telemetry and must not run for a client caller being relocated.
 *
 * Why this exists at all, rather than just dropping the two client roles from
 * the `/cost` NAV_ENTRIES row. That alone denies to `/home`, the wrong
 * destination: AITH-46 D-02 gave client roles a strict superset of AITH-47's
 * client-facing scope at `/usage`. It would also record an
 * `unauthorizedNavigation` event, which misreports a relocated destination as an
 * attempted access violation. The guard nonetheless preserves every query
 * parameter and fragment when targeting the truthful `/usage` destination.
 *
 * `roleGuard` still follows inside the composite, unchanged, and still fails
 * closed: it is what refuses a client role here should relocation be removed.
 *
 * Structural control, not a security boundary — enforcement is API-side, and the
 * cost operations already refuse a client-scoped caller with 403
 * (`CostAllocationController`, `CostByModelController`: `!IsAllTenant` →
 * `Forbid()`).
 */
export const costClientRedirect: CanActivateFn = (route) => {
  const session = inject(SessionService);
  if (!hasClientRole(session.session())) return true;

  // Pass the parameters through WHOLESALE. The filter URL surface is frozen
  // (FILTER_URL_PARAMS) and this guard does not own it — reconstructing by name
  // would silently drop any parameter added later.
  return new RedirectCommand(
    inject(Router).createUrlTree(['/usage'], {
      queryParams: route.queryParams,
      fragment: route.fragment ?? undefined,
    }),
  );
};
